/**
 * Validation for saving an in-progress application.
 *
 * Pure and network-free so it can be unit-tested directly, unlike the
 * dashboard closures it replaces.
 *
 * The split here is the whole point. `/apply` is a deliberately partial
 * intake (2026-08-10 slimming spec §1): it stores '' / null for sixteen
 * fields it never asks about, and the applicant finishes them in the
 * dashboard. So a blank is a normal state for a saved application, and
 * blocking a save on one strands the applicant's edits — which is exactly
 * what both dashboards used to do.
 *
 *   - BLANK  → never blocks a save. Surfaced by the completion meter, which
 *              the spec is explicit about: it "nags rather than blocks".
 *   - MALFORMED → blocks. A zip of '78' or an unparseable email is bad data,
 *              not absent data, and persisting it helps nobody.
 *
 * Completeness is still enforced where it actually matters: the client's
 * submit-to-attorney step, and the lawyer's Approve button, which is gated
 * separately by APPROVAL_REQUIRED_FIELDS.
 */

/** Fields the dashboard prompts for. Drives the completion meter only —
 *  never a save gate. Order is the order they appear in the form. */
export const CLIENT_REQUIRED_FIELDS = [
  'firstName', 'lastName',
  'address1', 'city', 'state', 'zip', 'phoneNumber', 'dateOfBirth',
  'caseType', 'requestedAmount', 'accidentState',
  'hasPriorFunding', 'hasOfferOrSettled',
  'insuranceCarrier', 'hasUmCoverage',
  'attorneyEmail', 'lawFirmName', 'lawFirmPhone',
  'hadPtOrChiro', 'hadSeriousInjections', 'brokeBones', 'hadSurgery',
  'childSupportLiens', 'inBankruptcy',
];

const ZIP = /^\d{5}(-\d{4})?$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** True when a value carries no answer. Whitespace is not an answer. */
const isBlank = (v) =>
  v === undefined || v === null || (typeof v === 'string' && v.trim() === '');

/**
 * Format and consistency errors only. An empty form is valid.
 *
 * @param {object|null} formData
 * @returns {Record<string, string>} field → message, empty when saveable
 */
export const validateApplicationFormats = (formData) => {
  const d = formData || {};
  const errors = {};

  if (!isBlank(d.zip) && !ZIP.test(String(d.zip).trim())) {
    errors.zip = 'Invalid format';
  }
  if (!isBlank(d.attorneyEmail) && !EMAIL.test(String(d.attorneyEmail).trim())) {
    errors.attorneyEmail = 'Invalid email';
  }
  if (!isBlank(d.requestedAmount)) {
    const amount = parseFloat(d.requestedAmount);
    if (Number.isNaN(amount) || amount <= 0) {
      errors.requestedAmount = 'Enter an amount greater than zero';
    }
  }

  // Companion fields: answering the parent is what makes these meaningful,
  // so an unanswered pair is inconsistent rather than merely incomplete.
  if (d.caseType === 'other' && isBlank(d.caseTypeOtherText)) {
    errors.caseTypeOtherText = 'Please describe your case type';
  }
  if (d.insuranceCarrier === 'other' && isBlank(d.insuranceCarrierOther)) {
    errors.insuranceCarrierOther = 'Please enter the carrier name';
  }
  if (d.hasUmCoverage === 'yes' && isBlank(d.umLimits)) {
    errors.umLimits = 'Enter the UM coverage limits';
  }

  return errors;
};

/**
 * Which required fields are still unanswered. For the completion meter and
 * the "finish your application" nudges — never for gating a save.
 *
 * @param {object|null} formData
 * @returns {string[]} field names, in CLIENT_REQUIRED_FIELDS order
 */
export const getMissingRequiredFields = (formData) => {
  if (!formData) return [...CLIENT_REQUIRED_FIELDS];
  return CLIENT_REQUIRED_FIELDS.filter((k) => isBlank(formData[k]));
};

/** Completion meter figures. Kept here so the meter and the nudges cannot
 *  drift apart from the list above. */
export const computeClientCompletion = (formData) => {
  const missing = getMissingRequiredFields(formData);
  const total = CLIENT_REQUIRED_FIELDS.length;
  const filled = total - missing.length;
  return { filled, total, pct: Math.round((filled / total) * 100), missingCount: missing.length };
};
